import { useEffect, useRef, useCallback, useMemo, useState } from 'react';

import { ApiError, type ApiClient } from '../api/client';
import { safeGitlabRequestId } from '../api/gitlabRequestId';
import type { ConversationGitlabInfo } from '../types';
import { gitlabReconnectDelay, gitlabTokenFailure, gitlabWebsocketFailure, type GitlabUpdatesFailure } from './gitlabUpdatesFailure';

interface GitlabUpdateItem {
  conversation_id: string;
  fetched_at: number;
  gitlab: ConversationGitlabInfo | null;
}

interface GitlabWsMessage {
  type: 'subscribed' | 'updates' | 'unsubscribed' | 'error';
  updates?: GitlabUpdateItem[];
  error?: string;
}

interface UseGitlabUpdatesOptions {
  apiClient: ApiClient;
  /** Conversation IDs to subscribe to */
  conversationIds: string[];
  /** Callback when GitLab info is updated */
  onUpdate: (conversationId: string, gitlab: ConversationGitlabInfo | null) => void;
  /** Whether the subscription is active */
  enabled?: boolean;
}

interface GitlabWsTokenResponse {
  token: string;
  request_id?: string | null;
}

/**
 * Hook to subscribe to GitLab updates via WebSocket.
 *
 * This hook establishes a WebSocket connection to receive real-time updates
 * when GitLab info changes. Failed connections surface a disconnected error.
 *
 * Usage:
 * ```tsx
 * useGitlabUpdates({
 *   conversationIds: conversations.map(c => c.id),
 *   onUpdate: (id, gitlab) => {
 *     setConversations(prev => prev.map(c =>
 *       c.id === id ? { ...c, gitlab } : c
 *     ));
 *   }
 * });
 * ```
 */
export function useGitlabUpdates({
  apiClient,
  conversationIds,
  onUpdate,
  enabled = true
}: UseGitlabUpdatesOptions): { failure: GitlabUpdatesFailure | null; retry: () => void } {
  const wsRef = useRef<WebSocket | null>(null);
  const subscribedIdsRef = useRef<Set<string>>(new Set());
  const onUpdateRef = useRef(onUpdate);
  const conversationIdsRef = useRef(conversationIds);
  const [failure, setFailure] = useState<GitlabUpdatesFailure | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const retry = useCallback(() => setRetryKey(key => key + 1), []);
  const conversationIdsKey = useMemo(() => conversationIds.join(','), [conversationIds]);

  // Keep callback ref updated
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    conversationIdsRef.current = conversationIds;
  }, [conversationIds]);

  // Stable callback for updates
  const handleUpdate = useCallback((conversationId: string, gitlab: ConversationGitlabInfo | null) => {
    onUpdateRef.current(conversationId, gitlab);
  }, []);

  useEffect(() => {
    const subscribedIds = subscribedIdsRef.current;

    if (!enabled || conversationIdsRef.current.length === 0) {
      setFailure(null);
      // Clean up if disabled
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
        subscribedIds.clear();
      }
      return;
    }

    let canceled = false;
    let reconnectTimer: number | null = null;
    let healthyTimer: number | null = null;
    let attempts = 0;
    let tokenRequestId: string | null = null;

    const scheduleReconnect = () => {
      const delay = gitlabReconnectDelay(attempts);
      if (delay === null) return false;
      attempts += 1;
      reconnectTimer = window.setTimeout(() => {
        void connect();
      }, delay);
      return true;
    };

    const connect = async () => {
      if (canceled) return;
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      let token = '';
      try {
        const response = await apiClient.post<GitlabWsTokenResponse>(
          '/api/conversations/gitlab/ws-token'
        );
        if (!response || typeof response.token !== 'string' || !response.token) {
          throw new ApiError('Invalid GitLab update token response', 200, undefined, undefined,
            'application/json');
        }
        token = response.token;
        tokenRequestId = safeGitlabRequestId(response.request_id) ?? null;
      } catch (err) {
        if (canceled) return;
        const retrying = scheduleReconnect();
        setFailure(gitlabTokenFailure(err, retrying));
        return;
      }

      if (canceled || !token) {
        return;
      }

      const params = new URLSearchParams({ ws_token: token });
      const wsUrl = `${wsProtocol}//${window.location.host}/api/conversations/gitlab/ws?${params.toString()}`;
      try {
        const ws = new WebSocket(wsUrl);
        let openedAt = 0;
        wsRef.current = ws;

        ws.onopen = () => {
          if (canceled) {
            ws.close();
            return;
          }
          openedAt = Date.now();
          // Do not hide the notice for a socket that opens and immediately
          // closes again. A stable connection clears it after one second.
          healthyTimer = window.setTimeout(() => {
            healthyTimer = null;
            if (!canceled && wsRef.current === ws) setFailure(null);
          }, 1000);
          // Subscribe to all conversations
          const newIds = conversationIdsRef.current.filter(id => !subscribedIds.has(id));
          if (newIds.length > 0) {
            ws.send(JSON.stringify({
              type: 'subscribe',
              conversation_ids: newIds
            }));
            newIds.forEach(id => subscribedIds.add(id));
          }
        };

        ws.onmessage = (event) => {
          if (canceled || wsRef.current !== ws) return;
          try {
            const msg: GitlabWsMessage = JSON.parse(event.data);
            if (msg.type === 'subscribed' || msg.type === 'updates') {
              for (const item of msg.updates ?? []) {
                handleUpdate(item.conversation_id, item.gitlab);
              }
            } else if (msg.type === 'error' && msg.error) {
              if (healthyTimer !== null) {
                window.clearTimeout(healthyTimer);
                healthyTimer = null;
              }
              setFailure(gitlabWebsocketFailure(null, tokenRequestId, false, msg.error));
            }
          } catch (err) {
            console.warn('Failed to parse gitlab ws message:', err);
          }
        };

        ws.onerror = () => {
          console.warn('GitLab updates websocket error');
          ws.close();
        };

        ws.onclose = (event) => {
          if (healthyTimer !== null) {
            window.clearTimeout(healthyTimer);
            healthyTimer = null;
          }
          if (canceled || wsRef.current !== ws) return;
          wsRef.current = null;
          subscribedIds.clear();
          // A socket that stayed open was healthy; an immediate open/close loop
          // must still consume the bounded reconnect attempts.
          if (openedAt && Date.now() - openedAt >= 30_000) attempts = 0;
          const retrying = scheduleReconnect();
          setFailure(gitlabWebsocketFailure(event, tokenRequestId, retrying));
        };
      } catch {
        console.warn('Failed to create GitLab updates WebSocket');
        const retrying = scheduleReconnect();
        setFailure(gitlabWebsocketFailure(null, tokenRequestId, retrying));
      }
    };

    void connect();

    return () => {
      canceled = true;
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
      }
      if (healthyTimer !== null) {
        window.clearTimeout(healthyTimer);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
        subscribedIds.clear();
      }
    };
  }, [apiClient, enabled, conversationIdsKey, handleUpdate, retryKey]);

  // Update subscriptions when conversationIds change
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const currentIds = subscribedIdsRef.current;
    const newIds = conversationIds.filter(id => !currentIds.has(id));
    const removedIds = Array.from(currentIds).filter(id => !conversationIds.includes(id));

    if (newIds.length > 0) {
      ws.send(JSON.stringify({
        type: 'subscribe',
        conversation_ids: newIds
      }));
      newIds.forEach(id => currentIds.add(id));
    }

    if (removedIds.length > 0) {
      ws.send(JSON.stringify({
        type: 'unsubscribe',
        conversation_ids: removedIds
      }));
      removedIds.forEach(id => currentIds.delete(id));
    }
  }, [conversationIds]);

  return { failure, retry };
}
